import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { STATES } from "@/components/leadUtils";
import { Building2, Check } from "lucide-react";

export default function Register() {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", brokerage: "" });
  const [territories, setTerritories] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate(user.role === "admin" ? "/admin" : "/dashboard", { replace: true });
  }, [user, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleTerritory = (code) =>
    setTerritories((t) => (t.includes(code) ? t.filter((x) => x !== code) : [...t, code]));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register({ ...form, territories });
      toast.success(`Welcome, ${u.name}! Pick your ZIP territories to start receiving leads.`);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080C14] text-white">
      <Navbar />
      <div className="max-w-md mx-auto px-4 py-12">
        <div className="text-center mb-8 fade-up">
          <div className="h-12 w-12 rounded-xl bg-emerald-400 flex items-center justify-center mx-auto mb-4 shadow-[0_0_20px_rgba(0,229,153,0.4)]">
            <Building2 className="h-6 w-6 text-slate-950" strokeWidth={2.5} />
          </div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Become a Premier Agent</h1>
          <p className="text-slate-400 mt-2 text-sm">Create your account, then pick your exclusive ZIP territories</p>
        </div>
        <Card className="bg-slate-900/90 border-slate-800 rounded-2xl p-8 backdrop-blur-xl">
          <form onSubmit={submit} className="space-y-4" data-testid="register-form">
            <div>
              <Label className="text-slate-400">Full name</Label>
              <Input data-testid="register-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} required className="bg-slate-950 border-slate-800 mt-1.5" placeholder="Jane Doe" />
            </div>
            <div>
              <Label className="text-slate-400">Email</Label>
              <Input data-testid="register-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required className="bg-slate-950 border-slate-800 mt-1.5" placeholder="jane@email.com" />
            </div>
            <div>
              <Label className="text-slate-400">Password</Label>
              <Input data-testid="register-password-input" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required minLength={6} className="bg-slate-950 border-slate-800 mt-1.5" placeholder="Min 6 characters" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-400">Phone</Label>
                <Input data-testid="register-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-slate-950 border-slate-800 mt-1.5" placeholder="Optional" />
              </div>
              <div>
                <Label className="text-slate-400">Brokerage</Label>
                <Input data-testid="register-brokerage-input" value={form.brokerage} onChange={(e) => set("brokerage", e.target.value)} className="bg-slate-950 border-slate-800 mt-1.5" placeholder="Optional" />
              </div>
            </div>
            <div>
              <Label className="text-slate-400 mb-2 block">Territories you cover</Label>
              <div className="grid grid-cols-2 gap-2">
                {STATES.map((s) => (
                  <button
                    key={s.code}
                    type="button"
                    data-testid={`register-territory-${s.code}`}
                    onClick={() => toggleTerritory(s.code)}
                    className={`flex items-center justify-between p-2.5 rounded-lg border text-sm transition-all ${
                      territories.includes(s.code)
                        ? "border-emerald-500 bg-emerald-500/10 text-white"
                        : "border-slate-800 bg-slate-950 text-slate-400 hover:border-slate-700"
                    }`}
                  >
                    {s.name}
                    {territories.includes(s.code) && <Check className="h-4 w-4 text-emerald-400" />}
                  </button>
                ))}
              </div>
            </div>
            <Button data-testid="register-submit-btn" type="submit" disabled={loading} className="w-full bg-emerald-400 text-slate-950 hover:bg-emerald-300 font-semibold rounded-full h-11">
              {loading ? "Creating account…" : "Start Free Trial"}
            </Button>
          </form>
          <p className="text-center text-sm text-slate-500 mt-6">
            Already an agent?{" "}
            <Link to="/login" className="text-emerald-400 hover:underline" data-testid="register-to-login-link">
              Sign in
            </Link>
          </p>
        </Card>
      </div>
    </div>
  );
}
